import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

// ── Types ──────────────────────────────────────────────────────────────────

interface SiteAssignment {
  assignmentId: number;
  siteId: number;
  companyName: string;
  assignedAt: string;
}

interface Manager {
  email: string;
  name: string | null;
  siteCount: number;
  sites: SiteAssignment[];
}

interface Site {
  siteId: number;
  companyName: string;
}

// ── Main component ─────────────────────────────────────────────────────────

export default function AccountManagerAssignments() {
  const api = useApi();

  // State
  const [managers, setManagers]     = useState<Manager[]>([]);
  const [sites, setSites]           = useState<Site[]>([]);
  const [loading, setLoading]       = useState(true);
  const [expanded, setExpanded]     = useState<string | null>(null);

  // Form state
  const [formEmail, setFormEmail]   = useState("");
  const [formName, setFormName]     = useState("");
  const [formSiteId, setFormSiteId] = useState<number | "">("");
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);

  // Search
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, sRes] = await Promise.all([
        api.admin.get("AccountManagerAssignments"),
        api.admin.get("AccountManagerAssignments/sites")
      ]);
      const mData = await mRes.json();
      const sData = await sRes.json();
      setManagers(Array.isArray(mData.managers) ? mData.managers : []);
      setSites(Array.isArray(sData) ? sData : []);
    } catch {
      setManagers([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const assign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formEmail || !formSiteId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.admin.post("AccountManagerAssignments", {
        siteId: formSiteId,
        managerEmail: formEmail.trim(),
        managerName: formName.trim() || null
      });
      if (!res.ok) {
        const body = await res.json();
        setError(body.error ?? "Failed to assign.");
      } else {
        setSuccess(`${formEmail} assigned to site ${formSiteId}.`);
        setFormSiteId("");
        await load();
      }
    } catch {
      setError("Request failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (assignmentId: number, companyName: string) => {
    if (!confirm(`Remove assignment for ${companyName}?`)) return;
    try {
      const res = await api.admin.delete(`AccountManagerAssignments/${assignmentId}`);
      if (res.ok) await load();
    } catch {
      setError("Failed to remove assignment.");
    }
  };

  const filteredManagers = managers.filter(m =>
    !search ||
    m.email.toLowerCase().includes(search.toLowerCase()) ||
    (m.name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  // Unassigned sites (not assigned to any active AM)
  const assignedSiteIds = new Set(managers.flatMap(m => m.sites.map(s => s.siteId)));
  const unassignedSites = sites.filter(s => !assignedSiteIds.has(s.siteId));

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Account Manager Assignments</h1>
        <p className="text-sm text-gray-500 mt-1">
          Assign Velaro account managers to customer sites. AMs receive a weekly health digest
          every Monday at 8am UTC showing their accounts sorted worst-first.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-6">

        {/* ── Left: Assign form ─────────────────────────────────────────── */}
        <div className="col-span-1">
          <div className="bg-white border rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Assign a Site</h2>
            <form onSubmit={assign} className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Manager Email *</label>
                <input
                  type="email"
                  required
                  value={formEmail}
                  onChange={e => setFormEmail(e.target.value)}
                  placeholder="am@velaro.com"
                  className="w-full border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Manager Name (optional)</label>
                <input
                  type="text"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="Jane Smith"
                  className="w-full border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Customer Site *</label>
                <select
                  required
                  value={formSiteId}
                  onChange={e => setFormSiteId(Number(e.target.value) || "")}
                  className="w-full border rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                >
                  <option value="">— select site —</option>
                  {sites.map(s => (
                    <option key={s.siteId} value={s.siteId}>
                      {s.companyName} (#{s.siteId})
                    </option>
                  ))}
                </select>
              </div>

              {error   && <p className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">{error}</p>}
              {success && <p className="text-xs text-green-700 bg-green-50 rounded px-2 py-1">{success}</p>}

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded transition-colors disabled:opacity-50"
              >
                {saving ? "Assigning…" : "Assign"}
              </button>
            </form>
          </div>

          {/* Unassigned sites callout */}
          {unassignedSites.length > 0 && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-amber-700 mb-1.5">
                {unassignedSites.length} unassigned site{unassignedSites.length !== 1 ? "s" : ""}
              </div>
              <div className="space-y-0.5 max-h-48 overflow-y-auto">
                {unassignedSites.map(s => (
                  <div
                    key={s.siteId}
                    className="flex items-center justify-between text-xs text-amber-800"
                  >
                    <span className="truncate">{s.companyName}</span>
                    <button
                      className="ml-2 text-blue-600 hover:underline shrink-0"
                      onClick={() => { setFormSiteId(s.siteId); setError(null); setSuccess(null); }}
                    >
                      Assign
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Right: Manager list ───────────────────────────────────────── */}
        <div className="col-span-2">
          <div className="flex items-center gap-3 mb-3">
            <input
              type="search"
              placeholder="Search managers…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border rounded px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <span className="text-xs text-gray-400 ml-auto">
              {managers.length} manager{managers.length !== 1 ? "s" : ""} · {managers.reduce((s, m) => s + m.siteCount, 0)} assignments
            </span>
          </div>

          {loading && (
            <div className="text-center py-12 text-gray-400">Loading…</div>
          )}

          {!loading && filteredManagers.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              {search ? "No managers match your search." : "No assignments yet. Assign a site to get started."}
            </div>
          )}

          <div className="space-y-2">
            {filteredManagers.map(m => (
              <div key={m.email} className="bg-white border rounded-lg overflow-hidden">
                {/* Manager header */}
                <button
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  onClick={() => setExpanded(expanded === m.email ? null : m.email)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold">
                      {(m.name ?? m.email).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {m.name ?? m.email}
                      </div>
                      {m.name && (
                        <div className="text-xs text-gray-500">{m.email}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      {m.siteCount} site{m.siteCount !== 1 ? "s" : ""}
                    </span>
                    <span className="text-gray-400 text-sm">{expanded === m.email ? "▲" : "▼"}</span>
                  </div>
                </button>

                {/* Site list */}
                {expanded === m.email && (
                  <div className="border-t">
                    {m.sites.map(s => (
                      <div
                        key={s.assignmentId}
                        className="flex items-center justify-between px-4 py-2.5 border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <div>
                          <span className="text-sm text-gray-800">{s.companyName}</span>
                          <span className="text-xs text-gray-400 ml-2">#{s.siteId}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-400">
                            {new Date(s.assignedAt).toLocaleDateString()}
                          </span>
                          <button
                            onClick={() => remove(s.assignmentId, s.companyName)}
                            className="text-xs text-red-500 hover:text-red-700 hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Quick-assign another site to this manager */}
                    <div className="px-4 py-2 bg-gray-50 flex items-center gap-2">
                      <span className="text-xs text-gray-500">Add site:</span>
                      <select
                        className="text-xs border rounded px-2 py-1 bg-white focus:outline-none"
                        value=""
                        onChange={async e => {
                          const sid = Number(e.target.value);
                          if (!sid) return;
                          setSaving(true);
                          setError(null);
                          try {
                            const res = await api.admin.post("AccountManagerAssignments", {
                              siteId: sid,
                              managerEmail: m.email,
                              managerName: m.name
                            });
                            if (!res.ok) {
                              const body = await res.json();
                              setError(body.error ?? "Failed.");
                            } else {
                              await load();
                            }
                          } catch { setError("Request failed."); }
                          finally { setSaving(false); }
                        }}
                      >
                        <option value="">— add site —</option>
                        {sites
                          .filter(s => !m.sites.some(ms => ms.siteId === s.siteId))
                          .map(s => (
                            <option key={s.siteId} value={s.siteId}>
                              {s.companyName} (#{s.siteId})
                            </option>
                          ))
                        }
                      </select>
                      {saving && <span className="text-xs text-gray-400">Saving…</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
